import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGmtClock } from '../hooks/useGmtClock.js';
import { useSiteLang } from '../hooks/useSiteLang.js';
import { fetchFlights, formatUpdatedLabel } from '../utils/flightsApi.js';
import {
  airlineLogoSrc,
  boardRowClass,
  boardStatusClass,
  DEFAULT_AIRLINE_LOGO,
  isIataAirlineCode,
  localizedStatus,
  placeLabel,
  searchHaystack,
  todayISO,
} from '../utils/flights.js';

function AirlineCell({ flight }) {
  return (
    <span className="flight-airline">
      <img
        className="flight-airline__logo"
        src={airlineLogoSrc(flight)}
        alt=""
        width="48"
        height="48"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={(e) => {
          e.currentTarget.src = DEFAULT_AIRLINE_LOGO;
        }}
      />
      <span className="visually-hidden">{flight.airline || 'Airline'}</span>
      {isIataAirlineCode(flight.airlineCode) && (
        <span className="flight-airline__code" aria-hidden="true">
          {String(flight.airlineCode).toUpperCase().replace(/[^A-Z0-9]/g, '')}
        </span>
      )}
    </span>
  );
}

function PlaceCell({ flight, dir, lang }) {
  const { city, code } = placeLabel(flight, dir, lang);
  return (
    <span className="notranslate" translate="no">
      {city}
      {code && code !== '—' && (
        <>
          {' '}
          <span className="flight-iata">({code})</span>
        </>
      )}
    </span>
  );
}

export default function FlightsPage() {
  const lang = useSiteLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDir = searchParams.get('dir') === 'dep' ? 'dep' : 'arr';
  const [board, setBoard] = useState(initialDir);
  const [dir, setDir] = useState(initialDir);
  const [q, setQ] = useState(searchParams.get('q') || searchParams.get('city') || '');
  const [date, setDate] = useState(searchParams.get('date') || todayISO());
  const [filterQ, setFilterQ] = useState(searchParams.get('q') || searchParams.get('city') || '');
  const [filterDate, setFilterDate] = useState(searchParams.get('date') || todayISO());
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedLabel, setUpdatedLabel] = useState('—');
  const { time } = useGmtClock();

  useEffect(() => {
    document.title = 'Flights | Nouakchott Oumtounsy International Airport (NKC/GQNO)';
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [arr, dep] = await Promise.all([fetchFlights('arrivals'), fetchFlights('departures')]);
        if (cancelled) return;
        setArrivals(arr);
        setDepartures(dep);
        setUpdatedLabel(formatUpdatedLabel([...arr, ...dep]));
      } catch {
        if (!cancelled) {
          setArrivals([]);
          setDepartures([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    setBoard(dir);
    setFilterQ(q);
    setFilterDate(date);
    const next = new URLSearchParams();
    next.set('dir', dir);
    if (q.trim()) next.set('q', q.trim());
    if (date) next.set('date', date);
    setSearchParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const list = board === 'dep' ? departures : arrivals;
    const qq = filterQ.trim().toLowerCase();
    return list.filter((f) => {
      if (qq && !searchHaystack(f, board === 'dep' ? 'dep' : 'arr').includes(qq)) return false;
      if (filterDate && f.date && f.date !== filterDate) return false;
      return true;
    });
  }, [board, arrivals, departures, filterQ, filterDate]);

  const colCount = board === 'dep' ? 8 : 7;

  return (
    <main id="main" className="flights-board-page">
      <section
        className="section flight-panel-wrap flight-panel-wrap--page flight-panel-wrap--light"
        id="flights-board"
        aria-labelledby="flight-panel-heading"
      >
        <div className="container">
          <header className="section__header">
            <h1 id="flight-panel-heading" className="section__title">
              Live flight board
            </h1>
            <p className="section__lead">
              Arrivals and departures in <strong>Greenwich Mean Time (GMT)</strong>, Nouakchott.
            </p>
          </header>

          <div className="flight-board-search-wrap">
            <form
              className="flight-search flight-search--enhanced flight-search--board"
              onSubmit={onSearch}
              role="search"
              aria-label="Flight search"
            >
              <div className="flight-search__bar">
                <div className="flight-search__dir">
                  <span className="flight-search__filter-label" id="flight-search-iam">
                    Direction
                  </span>
                  <div className="flight-search__segment" role="tablist" aria-labelledby="flight-search-iam">
                    <button
                      type="button"
                      className={`flight-search__tab${dir === 'arr' ? ' flight-search__tab--active' : ''}`}
                      role="tab"
                      aria-selected={dir === 'arr'}
                      onClick={() => setDir('arr')}
                    >
                      <span className="flight-search__tab-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 19V5M5 12l7-7 7 7" />
                        </svg>
                      </span>
                      <span className="flight-search__tab-text">Arriving</span>
                    </button>
                    <button
                      type="button"
                      className={`flight-search__tab${dir === 'dep' ? ' flight-search__tab--active' : ''}`}
                      role="tab"
                      aria-selected={dir === 'dep'}
                      onClick={() => setDir('dep')}
                    >
                      <span className="flight-search__tab-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12l7 7 7-7" />
                        </svg>
                      </span>
                      <span className="flight-search__tab-text">Departing</span>
                    </button>
                  </div>
                </div>

                <div className="flight-search__field-group flight-search__field-group--date">
                  <label className="flight-search__filter-label" htmlFor="flight-date">
                    Date
                  </label>
                  <input
                    type="date"
                    id="flight-date"
                    className="flight-search__date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>

                <div className="flight-search__field-group flight-search__field-group--grow">
                  <label className="flight-search__filter-label" htmlFor="flight-query">
                    Find a flight
                  </label>
                  <div className="flight-search__field flight-search__field--wide">
                    <svg
                      className="flight-search__field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      type="search"
                      id="flight-query"
                      className="flight-search__input"
                      placeholder="Flight number / Destination"
                      autoComplete="off"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flight-search__actions">
                  <span className="flight-search__filter-label flight-search__filter-label--spacer" aria-hidden="true">
                    &nbsp;
                  </span>
                  <button type="submit" className="flight-search__submit btn btn--accent">
                    Search
                  </button>
                </div>
              </div>
              <p className="flight-search__hint">
                Search by flight number, airline, origin, or destination · Times in <strong>GMT</strong>{' '}
                (Nouakchott)
              </p>
            </form>
          </div>

          <div className="flight-panel flight-panel--light" role="region" aria-label="Flight status table">
            <div className="flight-panel__toolbar">
              <div className="flight-panel__tabs" role="tablist">
                {[
                  { id: 'arr', label: 'Arrivals' },
                  { id: 'dep', label: 'Departures' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`flight-panel__tab${board === tab.id ? ' flight-panel__tab--active' : ''}`}
                    role="tab"
                    aria-selected={board === tab.id}
                    onClick={() => {
                      setBoard(tab.id);
                      setDir(tab.id);
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div
                className="flight-panel__clock-wrap"
                role="group"
                aria-label="Current time in Nouakchott, Greenwich Mean Time (GMT)"
              >
                <span className="flight-panel__tz-label">Nouakchott</span>
                <span className="flight-panel__clock">{time}</span>
                <span className="flight-panel__tz-badge">GMT</span>
              </div>
            </div>

            <p className="flight-panel__updated" aria-live="polite">
              Last updated: {updatedLabel}
            </p>

            <div className="table-scroll">
              <table className="flight-table">
                <caption className="visually-hidden">
                  {board === 'dep' ? 'Departures' : 'Arrivals'}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Airline</th>
                    <th scope="col">Flight</th>
                    <th scope="col">{board === 'dep' ? 'Destination' : 'Origin'}</th>
                    <th scope="col">Scheduled</th>
                    <th scope="col">Actual</th>
                    <th scope="col">Status</th>
                    {board === 'dep' ? (
                      <>
                        <th scope="col">Gate</th>
                        <th scope="col">Terminal</th>
                      </>
                    ) : (
                      <th scope="col">Baggage</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="flight-table__row flight-table__row--empty">
                      <td colSpan={colCount}>Loading flights…</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr className="flight-table__row flight-table__row--empty">
                      <td colSpan={colCount}>
                        No flights match your search. Try another flight number, airline, or city.
                      </td>
                    </tr>
                  ) : (
                    rows.map((f) => (
                      <tr
                        key={`${board}-${f.flight}-${f.sta || f.std}-${f.originCode || f.destinationCode}`}
                        className={boardRowClass(f.statusKey)}
                      >
                        <td data-label="Airline">
                          <AirlineCell flight={f} />
                        </td>
                        <td className="flight-cell-flight" data-label="Flight">
                          <strong>{f.flight}</strong>
                        </td>
                        <td data-label={board === 'dep' ? 'Destination' : 'Origin'}>
                          <PlaceCell flight={f} dir={board === 'dep' ? 'dep' : 'arr'} lang={lang} />
                        </td>
                        <td data-label="Scheduled">{board === 'dep' ? f.std : f.sta}</td>
                        <td data-label="Actual">
                          {board === 'dep'
                            ? (f.etd || '—') !== '—'
                              ? f.etd
                              : f.std
                            : (f.eta || '—') !== '—'
                              ? f.eta
                              : f.sta}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`${boardStatusClass(f.statusKey)} notranslate`}
                            translate="no"
                          >
                            {localizedStatus(f, lang)}
                          </span>
                        </td>
                        {board === 'dep' ? (
                          <>
                            <td data-label="Gate">{f.gate}</td>
                            <td data-label="Terminal">{f.terminal}</td>
                          </>
                        ) : (
                          <td data-label="Baggage">{f.baggage}</td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
